import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useHistory } from "react-router-dom";

import { GuideStatus } from "@mapmycustomers/shared";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import TourDescriptor, {
  TourStep,
  TourStepsStatus,
} from "@app/component/flows/types/TourDescriptor";
import getNextGuideStatus from "@app/component/flows/utils/getNextGuideStatus";
import { getFlowStatuses } from "@app/store/iam";
import { updateFlowStatus } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useGuides from "@app/util/contexts/useGuides";

import Tour from "./MmcTour";

export interface TourRunnerProps {
  descriptor: TourDescriptor;
}

const TourRunner = ({
  descriptor,
  onUpdateFlowStatus,
  status,
}: TourRunnerProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const history = useHistory();
  const { getCompletionPath, getGuideStatus, hideGuide, updateGuideStatus, visibility } =
    useGuides();

  const handleStatusChange = useCallback(
    (status: GuideStatus, force?: boolean) => {
      const guideStatus = getGuideStatus(descriptor.id);
      if (guideStatus) {
        const newGuideStatus = force ? status : getNextGuideStatus(guideStatus.status, status);
        if (newGuideStatus !== guideStatus.status) {
          updateGuideStatus({ ...guideStatus, status: newGuideStatus });
        }
      }
    },
    [descriptor.id, getGuideStatus, updateGuideStatus]
  );

  const handleHide = useCallback(() => {
    hideGuide(descriptor.id);
  }, [hideGuide, descriptor.id]);

  const handleTourComplete = useDynamicCallback(() => {
    const newStatus: TourStepsStatus = descriptor.steps.reduce(
      (result, step) => ({ ...result, [step.id]: true }),
      status ?? {}
    );
    onUpdateFlowStatus({ slug: descriptor.id, status: newStatus });
    handleStatusChange(GuideStatus.COMPLETED);
    if (descriptor.completionPath) {
      history.push(descriptor.completionPath);
    } else {
      const path = getCompletionPath(descriptor.id);
      // console.error("!!! completion path", path);
      if (path) {
        history.push(path);
      }
    }
    handleHide();
  });

  const handleStepComplete = useCallback(
    (id: TourDescriptor["id"], step: TourStep) => {
      const newStatus: TourStepsStatus = { ...(status ?? {}), [step.key ?? step.id]: true };
      onUpdateFlowStatus({ slug: id, status: newStatus });
      handleStatusChange(GuideStatus.PROGRESS);
    },
    [status, onUpdateFlowStatus, handleStatusChange]
  );

  return visibility[descriptor.id] ? (
    <Tour
      descriptor={descriptor}
      onComplete={handleTourComplete}
      onHide={handleHide}
      onStepComplete={handleStepComplete}
      visible
    />
  ) : null;
};

const mapStateToProps = (state: RootState, { descriptor }: TourRunnerProps) => ({
  status: getFlowStatuses(state)[descriptor.id] as TourStepsStatus | undefined,
});

const mapDispatchToProps = {
  onUpdateFlowStatus: updateFlowStatus.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TourRunner);
